import React, {lazy, Suspense, useEffect, useState, Fragment} from 'react';
import {
    Backdrop,
    CircularProgress,
    Grid,
    makeStyles,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core";
import {Alert} from '@material-ui/lab';

import useStyles from '../styles/styles';
import {useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {format} from 'date-fns';
import {navigate} from '@reach/router';

import LeadPurchaseRequestNavigationMenu from "../components/LeadPurchaseRequests/LeadPurchaseRequestNavigationMenu";
import CurrencyField from "../components/Fields/CurrencyField";
import PhoneField from "../components/Fields/PhoneField";
import SensitiveField from "../components/Fields/SensitiveField";
import BankInformation from "../components/Leads/BankInformation";
import LeadPurchaseResultInformation from "../components/LeadPurchaseRequests/LeadPurchaseResultInformation";
import LeadPurchaseResponseInformation from "../components/LeadPurchaseRequests/LeadPurchaseResponseInformation";
import LeadPurchaseDecisionInformation from "../components/LeadPurchaseRequests/LeadPurchaseDecisionInformation";
import LeadPurchasePriceRejectOfferInformation from "../components/LeadPurchaseRequests/LeadPurchasePriceRejectOfferInformation";
import LeadPurchaseVendorCampaignDecisionInformation from "../components/LeadPurchaseRequests/LeadPurchaseVendorCampaignDecisionInformation";
import LeadPurchaseMarketingRedirectInformation from "../components/LeadPurchaseRequests/LeadPurchaseMarketingRedirectInformation";
import setDate from '../utils/setDate';
import convertToCurrency from '../utils/convertToCurrency';
import dashes from 'add-dashes-to-uuid';
import ErrorBoundary from "../components/ErrorBoundary";
import RedirectDataTable from "../components/Redirect/RedirectDataTable";
import RequestLogTable from "../components/RequestLog/RequestLogTable";

const CloudwatchLogsTable = lazy(() => import("../components/CloudWatchLogs/CloudwatchLogsTable"));

makeStyles({
    root: {
        '& .MuiPaper-elevation1': {
            height: '400px',
            overflow: 'scroll'
        }
    },
});

const LeadPurchaseRequest = ({leadPurchaseRequestId}) => {
    const id = leadPurchaseRequestId.includes('-') ? leadPurchaseRequestId : dashes(leadPurchaseRequestId);
    const classes = useStyles();
    const [loadComponents, setLoadComponents] = useState(false);
    const {loading, error, data} = useQuery(gql`
        query LeadPurchaseRequestPage($leadPurchaseRequestId: String!) {
            leadPurchaseRequest(id: $leadPurchaseRequestId) {
                id
                createdAt
                clientIpAddress
                userAgent
                dateCreatedEffective
                isTestApp
                timeout
                sourceKey
                sourceLeadId
                sourceSessionId
                sourceSubCode1
                sourceSubCode1Hash
                sourceSubCode2
                sourceSubCode2Hash
                sourceSubCode3
                trafficSource {
                    id
                    name
                    medium
                    origin
                    channel
                    legacyZoneId
                    legacyPromoId
                    legacyOfferId
                    affiliate {
                        id
                        name
                        legacyPublisherId
                    }
                    payoutStrategyEnum
                }

                minimumLoanAmount
                maximumLoanAmount
                minimumPrice

                # Person data
                firstName
                middleName
                lastName
                primaryEmail
                primaryPhone
                homePhone
                mobilePhone
                workPhone
                workExt
                dateOfBirth
                military
                ssn
                ssnLastFour
                stateIdNumber
                stateIdNumberHash
                stateIdIssued

                # HomeAddress
                homeStreetAddress
                homeStreetAddress2
                homeCity
                homeState
                homeZip
                residenceType
                residenceLengthMonths

                # Employment Info
                employerName
                employerLengthMonths
                employmentType

                # Income Info
                payFrequency
                monthlyIncome
                nextPayDate
                secondPayDate
                payType

                # Bank Info
                bankName
                bankRouting
                bankAccount
                bankAccountType
                monthsAtBank
                
                titleAsCollateral
                titleClear
                
                referringSiteUrl

                response {
                    id
                    createdAt
                    publisherTimeout
                    billed
                    payout
                    status
                    responseTime
                    responseRendered
                    offerAmount
                }

                result {
                    id
                    requestReceivedAt
                    processCompletedAt
                    processTime
                    status
                    billed
                    payout
                    redirectUrl
                }

                leadPurchaseDecisions {
                    id
                    billable
                    payable
                    customerClassification
                    status
                    isListManageable                    
                    declineReason
                    newIdentity
                    returningCustomer
                    identityCanonicalId
                    offerId
                    redirectUrl
                    chanceToSell
                    chanceToSellElapsedInMillis
                    chanceToSellModelName
                    chanceToSellModelPrediction
                    soldCutoff
                    marketValue
                    marketValueElapsedInMillis
                    marketValueModelName
                    marketValueModelPrediction
                    priceStrategyId
                    priceStrategyName
                    purchasingStrategyId
                    purchasingStrategyName
                    soldCap
                    arbitrageMethod
                    companyId
                    adv1
                    adv2
                    adv3
                }

                leadPurchaseVendorCampaignDecisions {
                    id
                    timestamp
                    requestId
                    servicer
                    portfolio
                    campaignId
                    campaignType
                    status
                    pricePoint
                    billable
                    payable
                    request
                    response
                    result
                    requestTimestamp
                    responseTimestamp
                    statusCode
                    type
                    elapsedTime
                    vendorMessage
                }                
                
                offer {
                    id
                    originalRequestId
                    status
                    expiration
                    identityId
                    primaryEmail
                    offerAmount
                    redeemedRequestId
                    redeemedTimestamp
                }
                
                redirect {
                    id
                    dateCreated
                    secondsElapsed
                    leadPurchaseRequestId
                    clientIpAddress
                    clientIpVersion
                    destinationUrl
                    userAgent
                    tag
                }

                requestLog {
                    id
                    createdAt
                    message
                    olpDecision
                    olpSessionId
                    request
                    response
                    statusCode
                    uri
                }
                
                leadPurchaseMarketingRedirects {
                    id
                    leadPurchaseRequestId
                    popUnderUrl
                    localUrl
                }
            }
        }
    `, {
        variables: {
            leadPurchaseRequestId: id,
        }
    });

    useEffect(() => {
        setTimeout(() => {
            setLoadComponents(true);
        }, [3000])
    }, [data]);

    if (loading) {
        return (
            <Backdrop className={classes.backdrop} open={loading}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        )
    }

    if (error) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
                <p>
                    <a href="/#" onClick={(e) => {
                        e.preventDefault();
                        navigate('/info/leads');
                    }}>Click here to view all leads.</a>
                </p>
            </Paper>
        )
    }

    if (!data || !data.leadPurchaseRequest) {
        return (
            <>
                <Typography component="h2">
                    LeadPurchaseRequest data is invalid
                </Typography>
                <pre>${JSON.stringify(data, null, 2)}</pre>
            </>
        )
    }

    data.leadPurchaseRequest = data.leadPurchaseRequest || {}
    data.leadPurchaseRequest.dateOfBirth = new Date(data.leadPurchaseRequest.dateOfBirth)
    data.leadPurchaseRequest.nextPayDate = new Date(data.leadPurchaseRequest.nextPayDate)
    data.leadPurchaseRequest.secondPayDate = new Date(data.leadPurchaseRequest.secondPayDate)

    console.log("leadPurchaseRequest = ", data.leadPurchaseRequest)

    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace} id={"page-top"}>
                {id}
            </Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Created At" value={data.leadPurchaseRequest.createdAt ? setDate(data.leadPurchaseRequest.createdAt) : ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="IP Address" value={data.leadPurchaseRequest.clientIpAddress || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="User Agent" value={data.leadPurchaseRequest.userAgent || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Traffic Source" value={data.leadPurchaseRequest.trafficSource.name || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Minimum Price"
                                   value={data.leadPurchaseRequest.minimumPrice !== null ? convertToCurrency(data.leadPurchaseRequest.minimumPrice) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Minimum Loan Amount"
                                   value={data.leadPurchaseRequest.minimumLoanAmount !== null ? convertToCurrency(data.leadPurchaseRequest.minimumLoanAmount) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Maximum Loan Amount"
                                   value={data.leadPurchaseRequest.maximumLoanAmount !== null ? convertToCurrency(data.leadPurchaseRequest.maximumLoanAmount) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Channel" value={data.leadPurchaseRequest.trafficSource.channel || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Origin" value={data.leadPurchaseRequest.trafficSource.origin || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Medium" value={data.leadPurchaseRequest.trafficSource.medium || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Affiliate" value={data.leadPurchaseRequest.trafficSource.affiliate.name || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Payout Strategy"
                                   value={data.leadPurchaseRequest.trafficSource.payoutStrategyEnum || ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <TextField label="Affiliate Legacy Publisher ID"
                                   value={data.leadPurchaseRequest.trafficSource.affiliate.legacyPublisherId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Zone ID" value={data.leadPurchaseRequest.trafficSource.legacyZoneId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Promo ID" value={data.leadPurchaseRequest.trafficSource.legacyPromoId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Offer ID" value={data.leadPurchaseRequest.trafficSource.legacyOfferId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Timeout" value={data.leadPurchaseRequest.timeout || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={1}>
                        <TextField label="Is Test App" value={data.leadPurchaseRequest.isTestApp || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={4}>
                        <TextField label="Source Key" value={data.leadPurchaseRequest.sourceKey || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Source Lead Id" value={data.leadPurchaseRequest.sourceLeadId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Source Session Id" value={data.leadPurchaseRequest.sourceSessionId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Source Sub Code 1" value={data.leadPurchaseRequest.sourceSubCode1 || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Source Sub Code 1 Hash" value={data.leadPurchaseRequest.sourceSubCode1Hash || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Source Sub Code 2" value={data.leadPurchaseRequest.sourceSubCode2 || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Source Sub Code 2 Hash" value={data.leadPurchaseRequest.sourceSubCode2Hash || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Source Sub Code 3" value={data.leadPurchaseRequest.sourceSubCode3 || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <TextField label="Everflow Transaction Id" value={data.leadPurchaseRequest.everflowTransactionId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <TextField label="Referring Site URL" value={data.leadPurchaseRequest.referringSiteUrl || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Paper>


            <Typography variant="h4" component="h4" align="left">Processing Result</Typography>
            <LeadPurchaseResultInformation result={data.leadPurchaseRequest.result}/>

            <Typography variant="h4" component="h4" align="left" id={"person"}>Person</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="First Name" value={data.leadPurchaseRequest.firstName || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="Middle Name" value={data.leadPurchaseRequest.middleName || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="Last Name" value={data.leadPurchaseRequest.lastName || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={2} lg={1}>
                        <TextField label="Military" value={data.leadPurchaseRequest.military ? 'TRUE' : 'FALSE'} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={5}>
                        <TextField label="Email" value={data.leadPurchaseRequest.primaryEmail || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Primary Phone"
                                   value={data.leadPurchaseRequest.primaryPhone ? data.leadPurchaseRequest.primaryPhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Home Phone"
                                   value={data.leadPurchaseRequest.homePhone ? data.leadPurchaseRequest.homePhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Mobile Phone"
                                   value={data.leadPurchaseRequest.mobilePhone ? data.leadPurchaseRequest.mobilePhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Street Address" value={data.leadPurchaseRequest.homeStreetAddress || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={1}>
                        <TextField label="Line 2" value={data.leadPurchaseRequest.homeStreetAddress2 || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="City" value={data.leadPurchaseRequest.homeCity || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={3} lg={1}>
                        <TextField label="State" value={data.leadPurchaseRequest.homeState || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={3} lg={1}>
                        <TextField label="ZIP Code" value={data.leadPurchaseRequest.homeZip || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Residence Type" value={data.leadPurchaseRequest.residenceType || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Residence Length"
                                   value={`${data.leadPurchaseRequest.residenceLengthMonths} months` || ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Date of Birth" value={format(data.leadPurchaseRequest.dateOfBirth, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SensitiveField label="Social Security Number"
                                        value={data.leadPurchaseRequest.ssn ? data.leadPurchaseRequest.ssn.replace(/(\d{3})(\d{2})(\d{4})/, "$1–$2–$3") : ''}/>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={2}>
                        <SensitiveField label="State ID Number" value={data.leadPurchaseRequest.stateIdNumber || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="State ID Issued" value={data.leadPurchaseRequest.stateIdIssued || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <TextField label="State ID Hash" value={data.leadPurchaseRequest.stateIdNumberHash || ''} readOnly
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="h4" component="h4" align="left">Employment Information</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="Type" value={data.leadPurchaseRequest.employmentType || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Employment Length"
                                   value={data.leadPurchaseRequest.employerLengthMonths ? `${data.leadPurchaseRequest.employerLengthMonths} months` : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Employer Name" value={data.leadPurchaseRequest.employerName || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <PhoneField label="Phone" value={data.leadPurchaseRequest.workPhone || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="Ext" value={data.leadPurchaseRequest.workExt || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <CurrencyField label="Monthly Income" value={data.leadPurchaseRequest.monthlyIncome || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <TextField label="Pay Type" value={data.leadPurchaseRequest.payType || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Pay Frequency" value={data.leadPurchaseRequest.payFrequency || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Next Pay Date" value={format(data.leadPurchaseRequest.nextPayDate, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Second Pay Date" value={format(data.leadPurchaseRequest.secondPayDate, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                </Grid>
            </Paper>

            <BankInformation
                bankName={data.leadPurchaseRequest.bankName}
                accountType={data.leadPurchaseRequest.bankAccountType}
                routingNumber={data.leadPurchaseRequest.bankRouting}
                accountNumber={data.leadPurchaseRequest.bankAccount}
                monthsAtBank={data.leadPurchaseRequest.monthsAtBank}
            />

            <LeadPurchaseResponseInformation response={data.leadPurchaseRequest.response}/>
            <LeadPurchaseDecisionInformation decisions={data.leadPurchaseRequest.leadPurchaseDecisions}/>

            {data.leadPurchaseRequest.leadPurchaseVendorCampaignDecisions.length > 0 ? (
                <LeadPurchaseVendorCampaignDecisionInformation decisions={data.leadPurchaseRequest.leadPurchaseVendorCampaignDecisions}/>
            ) : null}

            {data.leadPurchaseRequest.offer ? (
                <LeadPurchasePriceRejectOfferInformation offer={data.leadPurchaseRequest.offer}/>
            ) : null}

            {data.leadPurchaseRequest.leadPurchaseMarketingRedirects.length > 0 ? (
                <LeadPurchaseMarketingRedirectInformation marketingRedirects={data.leadPurchaseRequest.leadPurchaseMarketingRedirects}/>
            ) : null}

            <RedirectDataTable redirectData={data.leadPurchaseRequest.redirect}/>
            <RequestLogTable requestLog={data.leadPurchaseRequest.requestLog}/>

            {loadComponents ? (
                <ErrorBoundary>
                    <Suspense fallback={<span></span>}>
                        <CloudwatchLogsTable
                            requestId={leadPurchaseRequestId}
                            requestTimestamp={data.leadPurchaseRequest.createdAt}
                        />
                    </Suspense>
                </ErrorBoundary>
            ) : null}

            <LeadPurchaseRequestNavigationMenu
                hasDecisionLog={data.leadPurchaseRequest.decision}
                hasRawRequestLogs={data.leadPurchaseRequest.requestLog}
                hasLoanRedirect={data.leadPurchaseRequest.redirect}
            />
        </>
    );
}

export default LeadPurchaseRequest
